import MojitoCore from 'mojito/core';
import MojitoPresentation from 'mojito/presentation';
import PrebuiltBet from './prebuilt-bet/index.js';
import PrebuiltBetsSkeleton from './skeleton/index.js';
import { memo, useEffect } from 'react';
import { isEmpty, range } from 'mojito/utils';

const classUtils = MojitoCore.Base.classUtils;
const { Swiper, LoaderSuspense, FlexPane, Text, EventCards } = MojitoPresentation.Components;
const { CardLoader } = EventCards;

// eslint-disable-next-line react/display-name
const PrebuiltBets = memo(props => {
    const {
        betItems,
        mojitoTools: { config, stringResolver },
        onPrepareCardsToRender,
        isContentLoaded,
        elementRef,
        class: className,
    } = props;
    const skeleton = <PrebuiltBetsSkeleton config={config.prebuiltBetsSkeleton} />;
    const headerLabel = isContentLoaded
        ? stringResolver.resolveString('$PREBUILT_BETS.TITLE_HEADER')
        : undefined;

    useEffect(() => {
        if (!config.showCarousel) {
            onPrepareCardsToRender(range(betItems.length));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [betItems.length, config.showCarousel]);

    if (config.skeletonLoader.disableLoader && isEmpty(betItems)) {
        return null;
    }
    return (
        <FlexPane
            class={classUtils.classNames('ta-PrebuiltBets', className)}
            config={config.container}
            elementRef={elementRef}
        >
            {config.showCarouselTitle && (
                <Text class="ta-PrebuiltBetsCarouselTitle" config={config.carouselTitle}>
                    {headerLabel}
                </Text>
            )}
            <LoaderSuspense
                config={config.skeletonLoader}
                isContentPending={!isContentLoaded}
                loader={skeleton}
            >
                <CardsContent {...props} />
            </LoaderSuspense>
        </FlexPane>
    );
});

const CardsContent = props => {
    const {
        mojitoTools: { config },
        betItems,
        isContentLoaded,
        onPrepareCardsToRender,
    } = props;

    return config.showCarousel ? (
        <FlexPane class="ta-PrebuiltBetsCarousel" config={config.carouselContainer}>
            <Swiper
                config={config.swiper}
                onPrepareSlidesToRender={onPrepareCardsToRender}
                hideNavigationArrows={betItems.length < config.minBetsForEnablingArrowNav}
            >
                {betItems.map(betInfo => rendererCardSlide(betInfo, config, isContentLoaded))}
            </Swiper>
        </FlexPane>
    ) : (
        <FlexPane class="ta-PrebuiltBetsContainer" config={config.betsContainer}>
            {betItems.map(betInfo => (
                <PrebuiltBet
                    key={betInfo.marketId}
                    config={config.prebuiltBet}
                    eventId={betInfo.eventId}
                    marketId={betInfo.marketId}
                    promotionText={betInfo.promotionText}
                    shouldRequestData={props.shouldRequestData}
                    boostedMarketId={betInfo.boostedMarketId}
                />
            ))}
        </FlexPane>
    );
};

function rendererCardSlide(betInfo, config, isContentLoaded) {
    return function CardSlide(readyToLoad) {
        return (
            <CardLoader key={betInfo.id} readyToLoad={readyToLoad}>
                <PrebuiltBet
                    config={config.prebuiltBet}
                    eventId={betInfo.eventId}
                    marketId={betInfo.marketId}
                    boostedMarketId={betInfo.boostedMarketId}
                    promotionText={betInfo.promotionText}
                    shouldRequestData={isContentLoaded}
                />
            </CardLoader>
        );
    };
}

export default PrebuiltBets;
